import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { Helmet } from 'react-helmet';
import AOS from 'aos';
import 'aos/dist/aos.css';

import { getLandingConfig } from '../utils/getLandingConfig';
import BusinessInfoForm from './BusinessInfoForm';
import { db, addDoc, collection, serverTimestamp, logEvent, analytics } from '../firebase';

const mapCategoryTitleToId = (title = '') => {
  const lower = String(title || '').toLowerCase();
  if (lower.includes('garage')) return 'garage';
  if (lower.includes('ventan')) return 'ventanas';
  return 'puertas';
};

const isVideoUrl = (url = '') => /\.(mp4|mov|m4v|webm|ogg)(\?.*)?$/i.test(url);
const isPdfUrl = (url = '') => /\.pdf(\?.*)?$/i.test(url);

const slugify = (text = '') =>
  text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '') || 'custom';

const buildFormCatalog = (categories = []) => {
  if (!Array.isArray(categories) || categories.length === 0) return null;
  const designPresets = { ventanas: [], puertas: [], garage: [] };
  const categoryMap = new Map();

  categories.forEach((category) => {
    const categoryTitle = category?.title || '';
    const categoryId = mapCategoryTitleToId(categoryTitle);
    if (!categoryId) return;
    if (!categoryMap.has(categoryId)) {
      categoryMap.set(categoryId, {
        id: categoryId,
        title: categoryTitle,
        desc: category?.desc || '',
      });
    }
    const styles = Array.isArray(category?.styles) ? category.styles : [];
    styles.forEach((style) => {
      const designId = slugify(style?.id || style?.name || style?.title || '');
      if (!designId) return;
      designPresets[categoryId] = [
        ...(designPresets[categoryId] || []),
        {
          id: designId,
          title: style?.name || style?.title || '',
          desc: style?.desc || '',
          image: Array.isArray(style?.images) ? style.images[0] || '' : style?.image || '',
        },
      ];
    });
  });

  const productCategories = Array.from(categoryMap.values());
  if (productCategories.length === 0) return null;
  return { productCategories, designPresets };
};

const LandingPage = () => {
  const [config, setConfig] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [galleryModal, setGalleryModal] = useState({
    open: false,
    title: '',
    images: [],
    current: 0,
  });
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantMessages, setAssistantMessages] = useState([]);
  const [assistantInput, setAssistantInput] = useState('');
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [assistantError, setAssistantError] = useState('');
  const [assistantSize, setAssistantSize] = useState('md');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardData, setWizardData] = useState({
    categoryId: '',
    categoryName: '',
    design: null,
    height: '',
    width: '',
    quantity: '1',
    series: '',
    color: '',
    glass: '',
    finish: '',
  });
  const [selectedModels, setSelectedModels] = useState([]);
  const [quoteModal, setQuoteModal] = useState({
    open: false,
    design: null,
    height: '',
    width: '',
    quantity: '1',
    series: '',
  });
  const [quoteModalError, setQuoteModalError] = useState('');
  const [quoteFormOpen, setQuoteFormOpen] = useState(false);

  // Registrar visita
  useEffect(() => {
    const sessionIdKey = 'sessionId';
    if (!sessionStorage.getItem(sessionIdKey)) {
      const newSessionId = Date.now().toString(36) + Math.random().toString(36).substring(2);
      sessionStorage.setItem(sessionIdKey, newSessionId);
    }

    const registerVisit = async () => {
      try {
        const sessionId = sessionStorage.getItem(sessionIdKey);
        if (analytics) logEvent(analytics, 'landing_visit');
        await addDoc(collection(db, 'analytics_events'), {
          type: 'visit',
          page: 'landing',
          source: document.referrer || 'direct',
          timestamp: serverTimestamp(),
          sessionId: sessionId || null,
        });
      } catch (err) {
        console.error('❌ Error registrando visita:', err);
      }
    };

    registerVisit();
  }, []);

  useEffect(() => {
    getLandingConfig().then(setConfig);
  }, []);

  useEffect(() => {
    AOS.init({ once: true });
  }, []);

  useEffect(() => {
    if (!config) return;
    if (typeof window === 'undefined') return;
    if (window.location.hash) {
      scrollToHash(window.location.hash);
    }
  }, [config]);

  useEffect(() => {
    if (!config) return;
    if (assistantMessages.length) return;
    const greeting = config.copy?.assistant?.greeting || '';
    setAssistantMessages([{ role: 'assistant', content: greeting }]);
  }, [config, assistantMessages.length]);

  const productCategories = Array.isArray(config?.productCategories) ? config.productCategories : [];
  const formCatalog = useMemo(() => buildFormCatalog(productCategories), [productCategories]);

  if (!config) return null;

  const brandName = config.siteName || '';
  const siteTitle = config.siteTitle || '';
  const metaDescription = config.metaDescription || '';
  const metaKeywords = config.metaKeywords || '';
  const theme = config.theme || {};
  const primary = theme.primary || '#0f5132';
  const primaryDark = theme.primaryDark || '#0b3d24';
  const gradientFrom = theme.gradientFrom || '#f4f7f4';
  const gradientTo = theme.gradientTo || '#ffffff';
  const highlight = theme.highlight || '#dfeee4';
  const cardBg = theme.cardBg || '#ffffff';
  const cardBorder = theme.cardBorder || '#d3e1d1';
  const background = theme.background || '#f9fafb';
  const surface = theme.surface || '#ffffff';
  const surfaceAlt = theme.surfaceAlt || '#f6faf6';
  const text = theme.text || '#1f2937';
  const muted = theme.muted || '#6b7280';
  const heroOverlayFrom = theme.heroOverlayFrom || '#02180e';
  const heroOverlayTo = theme.heroOverlayTo || '#03351d';
  const heroText = theme.heroText || '#ffffff';
  const heroMuted = theme.heroMuted || '#d1fae5';
  const heroCtaPrimaryBg = theme.heroCtaPrimaryBg || '';
  const heroCtaPrimaryText = theme.heroCtaPrimaryText || '#ffffff';
  const heroCtaSecondaryBg = theme.heroCtaSecondaryBg || '';
  const heroCtaSecondaryText = theme.heroCtaSecondaryText || '';
  const heroCtaSecondaryBorder = theme.heroCtaSecondaryBorder || '';
  const fontFamily = theme.fontFamily || 'ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif';
  const fontUrl = theme.fontUrl || '';
  const heroData = config.hero || {};
  const heroImage = heroData.image || '';
  const heroIsVideo = isVideoUrl(heroImage);
  const heroPretitle = heroData.pretitle || '';
  const heroTitle = heroData.title || '';
  const heroSubtitle = heroData.subtitle || '';
  const heroBullets = heroData.bullets || [];
  const heroCta = heroData.cta || '';
  const copy = config.copy || {};
  const headerCopy = copy.header || {};
  const heroCopy = copy.hero || {};
  const servicesCopy = copy.servicesSection || {};
  const productCopy = copy.productSection || {};
  const measurementCopy = copy.measurementSection || {};
  const featuredCopy = copy.featuredProductsSection || {};
  const impactCopy = copy.impactSection || {};
  const professionalCopy = copy.professionalSection || {};
  const testimonialCopy = copy.testimonialSection || {};
  const whyCopy = copy.whySection || {};
  const blogCopy = copy.blogSection || {};
  const aboutSection = config.aboutSection || {};
  const aboutImages = Array.isArray(aboutSection.images) ? aboutSection.images : [];
  const aboutHasContent =
    Boolean(aboutSection.eyebrow || aboutSection.title || aboutSection.subtitle || aboutSection.story) ||
    aboutImages.length > 0;
  const footerCopy = copy.footerSection || {};
  const contactCopy = copy.contactSection || {};
  const wizardCopy = copy.wizard || {};
  const modalsCopy = copy.modals || {};
  const assistantCopy = copy.assistant || {};
  const wizardOptions = config.wizardOptions || {};
  const wizardColorOptions = Array.isArray(wizardOptions.colors) ? wizardOptions.colors : [];
  const wizardGlassOptions = Array.isArray(wizardOptions.glass) ? wizardOptions.glass : [];
  const wizardFinishOptions = Array.isArray(wizardOptions.finish) ? wizardOptions.finish : [];
  const wizardSeriesOptions = Array.isArray(wizardOptions.series) ? wizardOptions.series : [];
  const floatingQuoteLabel = copy.floatingQuoteButton || '';
  const servicesTitle = config.servicesTitle || '';
  const services = Array.isArray(config.services) ? config.services : [];
  const navigationItems = Array.isArray(config.navigation) ? config.navigation : [];
  const featuredProductCards = Array.isArray(config.featuredProducts) ? config.featuredProducts : [];
  const impactHighlights = Array.isArray(config.impactHighlights) ? config.impactHighlights : [];
  const professionalSegments = Array.isArray(config.professionalSegments) ? config.professionalSegments : [];
  const showProfessionalSection = config.showProfessionalSection !== false;
  const showTestimonialSection = config.showTestimonialSection !== false;
  const showWhySection = config.showWhySection !== false;
  const showBlogSection = config.showBlogSection !== false;
  const showContactSection = config.showContactSection !== false;
  const whyChoose = Array.isArray(config.whyChoose) ? config.whyChoose : [];
  const blogPosts = Array.isArray(config.blogPosts) ? config.blogPosts : [];
  const contactInfo = config.contactInfo || {};
  const displayCategoryIndex =
    productCategories.length === 0 ? -1 : Math.min(activeCategoryIndex, productCategories.length - 1);
  const currentCategory = displayCategoryIndex >= 0 ? productCategories[displayCategoryIndex] : null;
  const wizardCategoryConfig = productCategories.find((cat) => mapCategoryTitleToId(cat.title || '') === wizardData.categoryId);
  const wizardStyles = wizardCategoryConfig?.styles || [];
  const wizardDesign = wizardData.design;

  const wizardStepTitles = Array.isArray(wizardCopy.steps) ? wizardCopy.steps : [];
  const wizardSteps = wizardStepTitles.map((title, index) => ({ id: index + 1, title }));
  const totalWizardSteps = wizardSteps.length;

  const getWizardOptionLabel = (options, value) => {
    const match = options.find((option) => option.id === value);
    return match?.label || value;
  };

  const getWizardOptionId = (options) => options[0]?.id || '';

  const wizardMeasurementsValid = Number(wizardData.height) > 0 && Number(wizardData.width) > 0 && Number(wizardData.quantity) > 0;
  const wizardCanProceed = () => {
    if (wizardStep === 1) return Boolean(wizardDesign);
    if (wizardStep === 2) return wizardMeasurementsValid;
    return true;
  };
  const goToNextWizardStep = () => {
    if (!wizardCanProceed()) return;
    if (wizardStep >= totalWizardSteps) {
      handleWizardAddModel();
    } else {
      setWizardStep((prev) => Math.min(totalWizardSteps, prev + 1));
    }
  };
  const goToPrevWizardStep = () => {
    setWizardStep((prev) => Math.max(1, prev - 1));
  };

  const withAlpha = (color, alpha) =>
    /^#([0-9a-f]{6})$/i.test(color) ? `${color}${alpha}` : color;

  const ctaBase =
    'inline-flex items-center justify-center rounded-full font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-70';
  const ctaPrimary = `${ctaBase} text-white shadow-lg hover:shadow-2xl`;
  const ctaSecondary = `${ctaBase} border hover:shadow-sm`;
  const ctaPrimaryStyle = { backgroundImage: `linear-gradient(120deg, ${primary}, ${primaryDark})` };
  const ctaSecondaryStyle = { borderColor: cardBorder, color: primary, backgroundColor: 'transparent' };
  const heroCtaBase = 'w-full sm:w-auto px-8 py-3 text-center';
  const heroCtaPrimaryClass = `${ctaPrimary} ${heroCtaBase}`;
  const heroCtaSecondaryClass = `${ctaSecondary} ${heroCtaBase}`;
  const heroPrimaryStyle = heroCtaPrimaryBg
    ? { backgroundColor: heroCtaPrimaryBg, color: heroCtaPrimaryText }
    : { backgroundImage: `linear-gradient(120deg, ${primary}, ${primaryDark})`, color: heroCtaPrimaryText };
  const heroSecondaryStyle = {
    borderColor: heroCtaSecondaryBorder || withAlpha(heroText, '66'),
    color: heroCtaSecondaryText || heroText,
    backgroundColor: heroCtaSecondaryBg || withAlpha(heroText, '1a'),
  };

  const handleWhatsAppClick = async () => {
    try {
      await addDoc(collection(db, 'analytics_events'), {
        type: 'whatsapp_click',
        page: 'landing',
        timestamp: new Date().toISOString(),
        sessionId: sessionStorage.getItem('sessionId') || null,
      });
      if (window.gtag) {
        window.gtag('event', 'whatsapp_click', {
          event_category: 'engagement',
          event_label: 'LandingPage',
        });
      }
    } catch (error) {
      console.error('❌ Error al registrar evento:', error);
    }
  };

  const buildDesignPayload = (category, style) => {
    if (!category || !style) return null;
    const categoryId = style.categoryId || category.id || mapCategoryTitleToId(category.title || '');
    return {
      categoryId,
      categoryName: category.title || style.categoryName || '',
      designId: slugify(style.id || style.name),
      name: style.name,
      description: style.desc,
      image: (style.images && style.images[0]) || '',
      gallery: style.images || [],
    };
  };

  const normalizeGallery = (images) => {
    if (!images) return [];
    if (Array.isArray(images)) {
      return images
        .map((item) => (typeof item === 'string' ? item : item?.url))
        .filter(Boolean);
    }
    if (typeof images === 'string') return [images];
    if (typeof images === 'object' && images?.url) return [images.url];
    return [];
  };

  const scrollToElement = (elementId) => {
    if (typeof document === 'undefined') return;
    const el = document.getElementById(elementId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToHash = (href) => {
    if (!href || typeof href !== 'string') return false;
    const hashIndex = href.indexOf('#');
    if (hashIndex === -1) return false;
    const targetId = href.slice(hashIndex + 1);
    if (!targetId) return false;
    const el = typeof document === 'undefined' ? null : document.getElementById(targetId);
    if (el) {
      scrollToElement(targetId);
      return true;
    }
    const aliasMap = {
      projects: 'services',
      project: 'services',
      service: 'services',
      servicios: 'services',
    };
    const alias = aliasMap[targetId];
    if (alias) {
      scrollToElement(alias);
      if (typeof window !== 'undefined') {
        window.history.replaceState(null, '', `#${alias}`);
      }
      return true;
    }
    return false;
  };

  const openQuoteFormModal = () => {
    setQuoteFormOpen(true);
  };

  const closeQuoteFormModal = () => {
    setQuoteFormOpen(false);
  };

  const openQuoteModalForDesign = (designPayload) => {
    const defaultSeries = getWizardOptionId(wizardSeriesOptions);
    setQuoteModal({
      open: true,
      design: designPayload,
      height: '',
      width: '',
      quantity: '1',
      series: designPayload?.categoryId === 'ventanas' ? defaultSeries : '',
    });
    setQuoteModalError('');
  };

  const closeQuoteModal = () => {
    setQuoteModal((prev) => ({ ...prev, open: false }));
    setQuoteModalError('');
  };

  const handleQuoteClick = (source, designPayload = null) => {
    if (designPayload) {
      openQuoteModalForDesign(designPayload);
    } else {
      setShowForm(true);
      setTimeout(() => scrollToElement('quote-form'), 120);
    }
    if (analytics) {
      logEvent(analytics, 'quote_cta_click', { source });
    }
  };

  const handleQuoteModalFieldChange = (field, value) => {
    setQuoteModal((prev) => ({ ...prev, [field]: value }));
  };

  const handleModalAddModel = () => {
    const { design, height, width, quantity, series } = quoteModal;
    if (!design) return;
    const heightNumber = Number(height);
    const widthNumber = Number(width);
    const quantityNumber = Number(quantity);

    if (!heightNumber || !widthNumber || !quantityNumber) {
      setQuoteModalError(modalsCopy.quoteModalError || '');
      return;
    }

    const defaultSeries = getWizardOptionId(wizardSeriesOptions);
    const modelId = `${design.designId}-${Date.now()}`;
    const modelPayload = {
      id: modelId,
      designId: design.designId,
      name: design.name,
      description: design.description,
      image: design.image,
      gallery: design.gallery,
      categoryId: design.categoryId,
      categoryName: design.categoryName || design.categoryId,
      height: String(heightNumber),
      width: String(widthNumber),
      quantity: String(quantityNumber),
      series: design.categoryId === 'ventanas' ? (series || defaultSeries || undefined) : undefined,
    };

    setSelectedModels((prev) => [...prev, modelPayload]);
    setQuoteModal({ open: false, design: null, height: '', width: '', quantity: '1', series: '' });
    setQuoteModalError('');
    openQuoteFormModal();
  };

  const getCategoryTitleById = (id = '') => {
    const match = productCategories.find((cat) => mapCategoryTitleToId(cat.title || '') === id || (cat.id && cat.id === id));
    return match?.title || '';
  };

  const getDefaultCategoryId = () => {
    const firstCategory = productCategories[0];
    return firstCategory?.id || mapCategoryTitleToId(firstCategory?.title || '');
  };

  const getDefaultDesignForCategory = (categoryId) => {
    const categoryObj = productCategories.find((cat) => mapCategoryTitleToId(cat.title || '') === categoryId);
    const baseStyle = categoryObj?.styles?.[0];
    return baseStyle ? buildDesignPayload({ id: categoryId, title: categoryObj.title }, { ...baseStyle, categoryId }) : null;
  };

  const openWizard = (designPayload = null) => {
    const categoryId = designPayload?.categoryId || getDefaultCategoryId();
    const design = designPayload || getDefaultDesignForCategory(categoryId);
    const categoryName = design?.categoryName || getCategoryTitleById(categoryId);
    const defaultSeries = getWizardOptionId(wizardSeriesOptions);
    setWizardData({
      categoryId,
      categoryName,
      design,
      height: '',
      width: '',
      quantity: '1',
      series: categoryId === 'ventanas' ? defaultSeries : '',
      color: getWizardOptionId(wizardColorOptions),
      glass: getWizardOptionId(wizardGlassOptions),
      finish: getWizardOptionId(wizardFinishOptions),
    });
    setWizardStep(design ? 2 : 1);
    setWizardOpen(true);
  };

  const closeWizard = () => {
    setWizardOpen(false);
    setWizardStep(1);
  };

  const handleWizardChange = (field, value) => {
    setWizardData((prev) => ({ ...prev, [field]: value }));
  };

  const handleWizardStyleSelect = (category, style) => {
    const payload = buildDesignPayload(category, { ...style, categoryId: category.id || category.categoryId });
    setWizardData((prev) => ({ ...prev, design: payload, categoryId: payload?.categoryId || prev.categoryId, categoryName: payload?.categoryName || prev.categoryName }));
    setWizardStep(2);
  };

  const handleWizardCategorySelect = (categoryId) => {
    const design = getDefaultDesignForCategory(categoryId);
    setWizardData((prev) => ({
      ...prev,
      categoryId,
      categoryName: getCategoryTitleById(categoryId),
      design,
      series: categoryId === 'ventanas' ? prev.series || getWizardOptionId(wizardSeriesOptions) : '',
    }));
  };

  const handleWizardAddModel = () => {
    const { design, height, width, quantity, series } = wizardData;
    if (!design) return;
    if (!Number(height) || !Number(width) || !Number(quantity)) return;
    const defaultSeries = getWizardOptionId(wizardSeriesOptions);
    const modelPayload = {
      id: `${design.designId}-${Date.now()}`,
      designId: design.designId,
      name: design.name,
      description: design.description,
      image: design.image,
      gallery: design.gallery,
      categoryId: design.categoryId,
      categoryName: design.categoryName,
      height: String(height),
      width: String(width),
      quantity: String(quantity),
      series: design.categoryId === 'ventanas' ? (series || defaultSeries || undefined) : undefined,
      color: wizardData.color,
      glass: wizardData.glass,
      finish: wizardData.finish,
    };

    setSelectedModels((prev) => [...prev, modelPayload]);
    closeWizard();
    openQuoteFormModal();
  };

  const renderWizardStep = () => {
    if (wizardStep === 1) {
      return (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-2">
            {productCategories.map((category) => {
              const categoryId = mapCategoryTitleToId(category.title || '');
              const active = wizardData.categoryId === categoryId;
              return (
                <button
                  key={category.title}
                  type="button"
                  onClick={() => handleWizardCategorySelect(categoryId)}
                  className={`${active ? ctaPrimary : ctaSecondary} text-sm px-4 py-2`}
                  style={active ? ctaPrimaryStyle : ctaSecondaryStyle}
                >
                  {category.title}
                </button>
              );
            })}
          </div>
          <div className="grid sm:grid-cols-2 gap-4 max-h-[360px] overflow-y-auto pr-2">
            {wizardStyles.length ? (
              wizardStyles.map((style) => {
                const designId = slugify(style.id || style.name);
                const isSelected = wizardDesign?.designId === designId;
                return (
                    <article
                      key={style.name}
                      className={`rounded-3xl border overflow-hidden shadow-sm cursor-pointer ${
                        isSelected ? 'border-emerald-500 shadow-lg' : 'border-gray-200'
                      }`}
                      style={{ borderColor: isSelected ? primary : cardBorder }}
                      onClick={() =>
                        handleWizardStyleSelect(
                          { id: wizardData.categoryId, title: wizardCategoryConfig?.title },
                          style
                        )
                      }
                  >
                    <img src={style.images?.[0]} alt={style.name} className="h-32 w-full object-cover" />
                      <div className="p-4 space-y-1">
                        <div className="flex items-center justify-between">
                          <p className="text-base font-semibold text-gray-900" style={{ color: text }}>{style.name}</p>
                          {isSelected && (
                            <span
                              className="text-xs font-semibold px-2 py-0.5 rounded-full"
                              style={{ color: primary, backgroundColor: surfaceAlt }}
                            >
                              {wizardCopy.selectedLabel || ''}
                            </span>
                          )}
                        </div>
                      <p className="text-sm text-gray-600" style={{ color: muted }}>{style.desc}</p>
                    </div>
                  </article>
                );
              })
            ) : (
              <p className="text-sm text-gray-500" style={{ color: muted }}>
                {wizardCopy.noStylesText || ''}
              </p>
            )}
          </div>
        </div>
      );
    }

    if (wizardStep === 2) {
      return (
        <div className="space-y-5">
          <div className="grid sm:grid-cols-3 gap-4">
            {['height', 'width', 'quantity'].map((field) => (
              <label key={field} className="text-sm font-semibold text-gray-700" style={{ color: text }}>
                {field === 'height' && (wizardCopy.heightLabel || '')}
                {field === 'width' && (wizardCopy.widthLabel || '')}
                {field === 'quantity' && (wizardCopy.quantityLabel || '')}
                <input
                  type="number"
                  min="1"
                  value={wizardData[field] ?? ''}
                  onChange={(e) => handleWizardChange(field, e.target.value)}
                  className="mt-1 w-full rounded-2xl border px-3 py-2"
                  style={{ borderColor: cardBorder }}
                />
              </label>
            ))}
          </div>
          {wizardData.categoryId === 'ventanas' && (
            <label className="text-sm font-semibold text-gray-700 block" style={{ color: text }}>
              {wizardCopy.seriesLabel || ''}
              <select
                value={wizardData.series}
                onChange={(e) => handleWizardChange('series', e.target.value)}
                className="mt-1 w-full rounded-2xl border px-3 py-2"
                style={{ borderColor: cardBorder }}
              >
                {wizardSeriesOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          {!wizardMeasurementsValid && (
          <p className="text-xs text-gray-500" style={{ color: muted }}>
            {wizardCopy.step2Hint || ''}
          </p>
          )}
        </div>
      );
    }

    if (wizardStep === 3) {
      return (
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { field: 'color', options: wizardColorOptions },
            { field: 'glass', options: wizardGlassOptions },
            { field: 'finish', options: wizardFinishOptions },
          ].map((item) => (
            <label key={item.field} className="text-sm font-semibold text-gray-700" style={{ color: text }}>
              {item.field === 'color' && (wizardCopy.step3ColorLabel || '')}
              {item.field === 'glass' && (wizardCopy.step3GlassLabel || '')}
              {item.field === 'finish' && (wizardCopy.step3FinishLabel || '')}
              <select
                value={wizardData[item.field]}
                onChange={(e) => handleWizardChange(item.field, e.target.value)}
                className="mt-1 w-full rounded-2xl border px-3 py-2"
                style={{ borderColor: cardBorder }}
              >
                {item.options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>
      );
    }

    const summaryEmptyValue = wizardCopy.summaryEmptyValue || '';
    const summaryUnitLabel = wizardCopy.summaryUnitLabel || '';
    const summaryQuantityValue = wizardData.quantity || summaryEmptyValue;
    const summaryQuantityText = summaryUnitLabel
      ? `${summaryQuantityValue} ${summaryUnitLabel}`
      : summaryQuantityValue;
    const summarySeriesValue = getWizardOptionLabel(wizardSeriesOptions, wizardData.series || '');
    const summaryColorValue = getWizardOptionLabel(wizardColorOptions, wizardData.color || '');
    const summaryGlassValue = getWizardOptionLabel(wizardGlassOptions, wizardData.glass || '');
    const summaryFinishValue = getWizardOptionLabel(wizardFinishOptions, wizardData.finish || '');

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          {wizardDesign?.image && (
            <img src={wizardDesign.image} alt={wizardDesign.name} className="h-24 w-24 rounded-2xl object-cover" />
          )}
          <div>
            <p className="text-xs uppercase tracking-wide" style={{ color: primary }}>{wizardDesign?.categoryName}</p>
            <p className="text-lg font-semibold text-gray-900" style={{ color: text }}>
              {wizardDesign?.name || wizardCopy.summaryTitleFallback || ''}
            </p>
            <p className="text-sm text-gray-500" style={{ color: muted }}>{wizardDesign?.description}</p>
          </div>
        </div>
        <div
          className="rounded-3xl border p-4 text-sm space-y-1"
          style={{ borderColor: cardBorder, backgroundColor: surfaceAlt, color: muted }}
        >
          <p>
            <strong>{wizardCopy.summaryMeasurementsLabel || ''}:</strong>{' '}
            {wizardData.height || summaryEmptyValue}" x {wizardData.width || summaryEmptyValue}" · {summaryQuantityText}
          </p>
          {wizardData.series && (
            <p>
              <strong>{wizardCopy.summarySeriesLabel || ''}:</strong> {summarySeriesValue}
            </p>
          )}
          <p>
            <strong>{wizardCopy.summaryFinishLabel || ''}:</strong>{' '}
            {summaryColorValue} / {summaryGlassValue} / {summaryFinishValue}
          </p>
          <p className="text-xs" style={{ color: primary }}>
            {wizardCopy.summaryHelper || ''}
          </p>
        </div>
      </div>
    );
  };

  const sendAssistantMessage = async () => {
    const content = assistantInput.trim();
    if (!content) return;

    const updatedMessages = [...assistantMessages, { role: 'user', content }];
    setAssistantMessages(updatedMessages);
    setAssistantInput('');
    setAssistantLoading(true);
    setAssistantError('');

    try {
      const historyPayload = updatedMessages.slice(-6).map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      const response = await fetch('https://us-central1-prventanas.cloudfunctions.net/aiAssistantReply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: content,
          history: historyPayload,
          context: {
            page: 'landing',
            source: 'assistant_modal',
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const reply =
        typeof data.reply === 'string'
          ? data.reply
          : typeof data.reply === 'object'
          ? JSON.stringify(data.reply)
          : assistantCopy.fallbackReply || '';

      setAssistantMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (error) {
      console.error('❌ Error asistente AI:', error);
      setAssistantMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: assistantCopy.errorReply || '',
        },
      ]);
      setAssistantError(assistantCopy.errorNotice || '');
    } finally {
      setAssistantLoading(false);
    }
  };

  const openGalleryModal = (title, images, startIndex = 0) => {
    const normalizedImages = normalizeGallery(images);
    setGalleryModal({
      open: true,
      title,
      images: normalizedImages,
      current: Math.min(startIndex, Math.max(0, normalizedImages.length - 1)),
    });
  };

  const closeGalleryModal = () => {
    setGalleryModal((prev) => ({ ...prev, open: false }));
  };

  const handleGalleryNav = (direction) => {
    setGalleryModal((prev) => {
      const total = prev.images.length;
      if (total === 0) return prev;
      let nextIndex = prev.current;
      if (direction === 'next') {
        nextIndex = (prev.current + 1) % total;
      } else if (direction === 'prev') {
        nextIndex = (prev.current - 1 + total) % total;
      }
      return { ...prev, current: nextIndex };
    });
  };

  const activeGalleryItem = galleryModal.images[galleryModal.current] || '';
  const activeGalleryIsVideo = isVideoUrl(activeGalleryItem);
  const activeGalleryIsPdf = isPdfUrl(activeGalleryItem);

  return (
    <>
      <Helmet>
        <title>{siteTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={metaKeywords} />
        <script async src={`https://www.googletagmanager.com/gtag/js?id=${config.googleTagId}`}></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${config.googleTagId}');
          `}
        </script>
        {fontUrl && <link rel="stylesheet" href={fontUrl} />}
      </Helmet>

      <div className="min-h-screen" style={{ backgroundColor: background, color: text, fontFamily }}>
        <header
          className="border-b py-3 shadow-sm sticky top-0 z-30"
          style={{ backgroundColor: surface, borderColor: cardBorder }}
        >
          <div className="max-w-7xl mx-auto px-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={config.logo} alt={brandName || ''} className="h-10 w-auto object-contain" />
                <div>
                  <p className="font-bold text-lg text-gray-900" style={{ color: text }}>{brandName}</p>
                  <p className="text-xs text-gray-500" style={{ color: muted }}>
                    {headerCopy.tagline || ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="hidden md:flex items-center gap-2 text-sm text-gray-600" style={{ color: muted }}>
                  <span className="uppercase tracking-[0.2em] text-xs">{headerCopy.marketsLabel || ''}</span>
                  {(headerCopy.markets || []).map((market, index) => (
                    <button
                      key={`${market}-${index}`}
                      className="px-3 py-1 rounded-full border text-xs font-semibold"
                      style={{
                        borderColor: cardBorder,
                        backgroundColor: index === 0 ? surfaceAlt : surface,
                        color: index === 0 ? primary : text,
                      }}
                    >
                      {market}
                    </button>
                  ))}
                </div>
                <button
                  className="md:hidden p-2 rounded-full border"
                  style={{ borderColor: cardBorder }}
                  onClick={() => setMobileNavOpen((prev) => !prev)}
                  aria-label={headerCopy.menuLabel || ''}
                >
                  <span className="block w-5 h-0.5 mb-1" style={{ backgroundColor: muted }}></span>
                  <span className="block w-5 h-0.5 mb-1" style={{ backgroundColor: muted }}></span>
                  <span className="block w-5 h-0.5" style={{ backgroundColor: muted }}></span>
                </button>
              </div>
            </div>
            <div className="hidden md:flex items-center justify-between gap-6">
              <nav className="flex-1 flex items-center gap-6 text-sm font-semibold text-gray-700" style={{ color: text }}>
                {navigationItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    className="py-2 hover:opacity-80 transition"
                    style={{ color: text }}
                    onClick={(e) => {
                      if (scrollToHash(item.href)) {
                        e.preventDefault();
                      }
                    }}
                  >
                    <div>{item.label}</div>
                    <p className="text-xs font-normal text-gray-500" style={{ color: muted }}>{item.sublabel}</p>
                  </a>
                ))}
              </nav>
              <div className="flex items-center gap-3">
                <a
                  href="/login"
                  className="px-5 py-2 rounded-full text-sm font-semibold border transition"
                  style={{ borderColor: cardBorder, color: text }}
                >
                  {headerCopy.loginLabel || ''}
                </a>
                <button
                  onClick={() => handleQuoteClick('header')}
                  className="text-white px-5 py-2 rounded-full text-sm font-semibold shadow hover:shadow-lg transition"
                  style={ctaPrimaryStyle}
                >
                  {headerCopy.quoteLabel || ''}
                </button>
              </div>
            </div>
            {mobileNavOpen && (
              <div className="md:hidden border-t pt-3 space-y-3" style={{ borderColor: cardBorder }}>
                {navigationItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    className="block text-sm font-semibold"
                    style={{ color: text }}
                    onClick={(e) => {
                      if (scrollToHash(item.href)) {
                        e.preventDefault();
                      }
                      setMobileNavOpen(false);
                    }}
                  >
                    {item.label}
                    <p className="text-xs font-normal text-gray-500" style={{ color: muted }}>{item.sublabel}</p>
                  </a>
                ))}
                <a
                  href="/login"
                  className="w-full text-center text-sm font-semibold border rounded-full py-2"
                  style={{ borderColor: cardBorder, color: text }}
                  onClick={() => setMobileNavOpen(false)}
                >
                  {headerCopy.loginLabel || ''}
                </a>
                <a
                  href="/ecommerce"
                  className="w-full text-center text-sm font-semibold border rounded-full py-2"
                  style={{ borderColor: cardBorder, color: primary, backgroundColor: surfaceAlt }}
                  onClick={() => setMobileNavOpen(false)}
                >
                  {headerCopy.ecommerceLabel || ''}
                </a>
                <button
                  onClick={() => {
                    setMobileNavOpen(false);
                    handleQuoteClick('header-mobile');
                  }}
                  className="w-full text-white px-5 py-2 rounded-full text-sm font-semibold shadow hover:shadow-lg transition"
                  style={ctaPrimaryStyle}
                >
                  {headerCopy.quoteLabel || ''}
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="pb-16">
          <section
            id="hero"
            className="relative py-24 px-4 overflow-hidden"
            style={{
              backgroundImage: heroIsVideo
                ? 'none'
                : heroImage
                  ? `linear-gradient(120deg, ${withAlpha(heroOverlayFrom, 'b3')}, ${withAlpha(heroOverlayTo, 'bf')}), url("${heroImage}")`
                  : `linear-gradient(120deg, ${withAlpha(heroOverlayFrom, 'b3')}, ${withAlpha(heroOverlayTo, 'bf')})`,
              backgroundSize: heroIsVideo ? undefined : 'cover',
              backgroundPosition: heroIsVideo ? undefined : 'center',
              color: heroText,
            }}
          >
            {heroIsVideo && (
              <>
                <video
                  className="absolute inset-0 w-full h-full object-cover"
                  src={heroImage}
                  autoPlay
                  muted
                  loop
                  playsInline
                />
                <div
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `linear-gradient(120deg, ${withAlpha(heroOverlayFrom, 'b3')}, ${withAlpha(heroOverlayTo, 'bf')})`,
                  }}
                  aria-hidden="true"
                />
              </>
            )}
            <div className="max-w-5xl mx-auto text-center space-y-6 relative z-10" data-aos="fade-up">
              <p className="text-xs uppercase tracking-[0.6em]" style={{ color: heroMuted }}>{heroPretitle}</p>
              <h1 className="text-4xl md:text-5xl font-extrabold">
                {heroTitle}
              </h1>
              <p className="text-lg" style={{ color: heroMuted }}>
                {heroSubtitle}
              </p>
              {heroBullets.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2">
                  {heroBullets.map((bullet, idx) => (
                    <span
                      key={`${bullet}-${idx}`}
                      className="px-3 py-1 rounded-full text-xs sm:text-sm border"
                      style={{
                        color: heroMuted,
                        borderColor: withAlpha(heroMuted, '66'),
                        backgroundColor: withAlpha(heroText, '1a'),
                      }}
                    >
                      {bullet}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3">
                <button
                  onClick={() => handleQuoteClick('hero-primary')}
                  className={heroCtaPrimaryClass}
                  style={heroPrimaryStyle}
                >
                  {heroCta}
                </button>
                <a
                  href="/login"
                  className={heroCtaSecondaryClass}
                  style={heroSecondaryStyle}
                >
                  {heroCopy.loginLabel || ''}
                </a>
                <a
                  href="/ecommerce"
                  className={heroCtaSecondaryClass}
                  style={heroSecondaryStyle}
                >
                  {heroCopy.ecommerceLabel || ''}
                </a>
              </div>
            </div>
            {showForm && (
              <div
                className="mt-12 max-w-4xl mx-auto backdrop-blur border rounded-3xl shadow-2xl p-6"
                style={{ backgroundColor: withAlpha(cardBg, 'f2'), borderColor: cardBorder }}
                id="quote-form"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm uppercase tracking-wide text-gray-500" style={{ color: muted }}>
                      {heroCopy.quickFormLabel || ''}
                    </p>
                    <h3 className="text-2xl font-bold text-gray-900" style={{ color: text }}>
                      {heroCopy.quickFormTitle || ''}
                    </h3>
                  </div>
                  <button
                    onClick={() => setShowForm(false)}
                    className="text-sm text-gray-500 hover:opacity-80"
                    style={{ color: muted }}
                  >
                    {heroCopy.quickFormClose || ''}
                  </button>
                </div>
                <BusinessInfoForm
                  selectedModels={selectedModels}
                  onUpdateSelectedModels={setSelectedModels}
                  catalog={formCatalog || undefined}
                />
              </div>
            )}
          </section>

          <div id="service" />
          <div id="servicios" />
          <section id="services" className="py-16 px-4 border-t" style={{ backgroundColor: surface, borderColor: highlight }}>
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-10">
                <p className="text-xs uppercase tracking-[0.4em]" style={{ color: muted }}>
                  {servicesCopy.eyebrow || ''}
                </p>
                <h2 className="text-3xl font-bold" style={{ color: text }}>{servicesTitle}</h2>
                <p className="mt-2" style={{ color: muted }}>
                  {servicesCopy.subtitle || ''}
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {services.map((service, index) => {
                  const galleryItems = normalizeGallery(service.gallery);
                  return (
                  <article
                    key={service.title || index}
                    className="border rounded-3xl overflow-hidden shadow-sm hover:shadow-lg transition"
                    style={{ borderColor: cardBorder, backgroundColor: cardBg }}
                  >
                    {service.src && (
                      <img src={service.src} alt={service.title || ''} className="h-52 w-full object-cover" />
                    )}
                    <div className="p-6 space-y-3">
                      <h3 className="text-xl font-semibold" style={{ color: text }}>{service.title}</h3>
                      <p className="text-sm" style={{ color: muted }}>{service.desc}</p>
                      {galleryItems.length > 0 && (
                        <button
                          type="button"
                          onClick={() => openGalleryModal(service.title || modalsCopy.galleryLabel || '', galleryItems)}
                          className="text-sm font-semibold"
                          style={{ color: primary }}
                        >
                          {servicesCopy.galleryLabel || ''}
                        </button>
                      )}
                    </div>
                  </article>
                );
                })}
              </div>
            </div>
          </section>

          <section className="py-16 px-4 border-t" style={{ borderColor: highlight, backgroundColor: surface }}>
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-8">
                <p className="text-xs uppercase tracking-[0.4em] text-gray-500" style={{ color: muted }}>
                  {productCopy.eyebrow || ''}
                </p>
                <h2 className="text-3xl font-bold text-gray-900" style={{ color: text }}>
                  {productCopy.title || ''}
                </h2>
                <p className="mt-2" style={{ color: muted }}>
                  {productCopy.subtitle || ''}
                </p>
              </div>
              <div className="flex flex-wrap justify-center gap-3 mb-8">
                {productCategories.map((category, index) => (
                  <button
                    key={category.title}
                    onClick={() => setActiveCategoryIndex(index)}
                    className="px-4 py-2 rounded-full text-sm font-semibold border"
                    style={
                      displayCategoryIndex === index
                        ? { backgroundColor: primary, borderColor: primary, color: '#ffffff' }
                        : { backgroundColor: surface, borderColor: cardBorder, color: text }
                    }
                  >
                    {category.title}
                  </button>
                ))}
              </div>
              {currentCategory && (
                <article className="rounded-3xl border shadow-sm transition p-4 md:p-6" style={{ borderColor: cardBorder, backgroundColor: cardBg }}>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-2" style={{ color: text }}>
                    {currentCategory.title}
                  </h3>
                  <p className="text-sm mb-6" style={{ color: muted }}>{currentCategory.desc}</p>
                  <div className="lg:hidden flex items-center justify-center gap-2 text-xs font-semibold mb-3" style={{ color: muted }}>
                    <span>Desliza para ver más modelos</span>
                    <span className="scroll-hint-arrow">→</span>
                  </div>
                  <div className="overflow-x-auto">
                    <div className="flex gap-4 md:gap-6">
                      {(currentCategory.styles || []).map((style) => {
                        const categoryId = mapCategoryTitleToId(currentCategory.title || '');
                        const designPayload = buildDesignPayload(
                          { id: categoryId, title: currentCategory.title },
                          { ...style, categoryId }
                        );
                        return (
                          <div
                            key={style.name}
                            className="min-w-[240px] max-w-xs flex-shrink-0 border rounded-2xl p-4 flex flex-col gap-3"
                            style={{ backgroundColor: surfaceAlt, borderColor: cardBorder }}
                          >
                            <div>
                              <h4 className="text-lg font-semibold text-gray-900" style={{ color: text }}>{style.name}</h4>
                              <p className="text-sm text-gray-600" style={{ color: muted }}>{style.desc}</p>
                            </div>
                            <div className="flex gap-2">
                              {(style.images || []).slice(0, 2).map((img, idx) => (
                                <div key={idx} className="w-1/2 overflow-hidden rounded-xl shadow-sm">
                                  <img
                                    src={img}
                                    alt={`${style.name}-${idx}`}
                                    className="h-20 w-full object-cover hover:scale-105 transition cursor-pointer"
                                    onClick={() =>
                                      openGalleryModal(`${currentCategory.title} · ${style.name}`, style.images || [], idx)
                                    }
                                  />
                                </div>
                              ))}
                            </div>
                            <div className="mt-auto space-y-2">
                              <button
                                onClick={() => handleQuoteClick(`style-${designPayload?.designId || style.name}`, designPayload)}
                                className={`${ctaPrimary} w-full text-sm py-2`}
                                style={ctaPrimaryStyle}
                              >
                                {productCopy.quoteLabel || ''}
                                {productCopy.quoteLabel ? ' ' : ''}
                                {style.name}
                              </button>
                              <button
                                onClick={() =>
                                  openGalleryModal(`${productCategories[activeCategoryIndex].title} · ${style.name}`, style.images)
                                }
                                disabled={!style.images || style.images.length === 0}
                                className="w-full rounded-full border text-sm font-semibold py-2"
                                style={{ borderColor: cardBorder, color: primary }}
                              >
                                {productCopy.galleryLabel || ''}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </article>
              )}
            </div>
          </section>

          <section id="quote" className="py-8 px-4" style={{ borderBottom: `1px solid ${highlight}`, backgroundColor: surfaceAlt }}>
            <div className="max-w-4xl mx-auto flex justify-center">
              <button
                type="button"
                onClick={openQuoteFormModal}
                className={`${ctaPrimary} px-6 py-2.5 text-sm`}
                style={ctaPrimaryStyle}
              >
                Cotizar ahora
              </button>
            </div>
          </section>

          <section id="products" className="py-16 px-4 border-t" style={{ borderColor: highlight, backgroundColor: surface }}>
            <div className="max-w-6xl mx-auto text-center mb-10">
              <p className="text-xs uppercase tracking-[0.4em] text-gray-500" style={{ color: muted }}>
                {featuredCopy.eyebrow || ''}
              </p>
              <h2 className="text-3xl font-bold text-gray-900" style={{ color: text }}>
                {featuredCopy.title || ''}
              </h2>
              <p className="mt-2" style={{ color: muted }}>
                {featuredCopy.subtitle || ''}
              </p>
            </div>
            <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
              {featuredProductCards.map((product) => (
                <article
                  key={product.title}
                  className="border rounded-3xl overflow-hidden shadow-sm hover:shadow-lg transition"
                  style={{ borderColor: cardBorder, backgroundColor: cardBg }}
                >
                  <img src={product.image} alt={product.title} className="h-56 w-full object-cover" />
                  <div className="p-6 space-y-3 text-left">
                    <p className="text-xs uppercase tracking-[0.4em] text-gray-500" style={{ color: muted }}>{product.title}</p>
                    <h3 className="text-2xl font-semibold text-gray-900" style={{ color: text }}>{product.subtitle}</h3>
                    <p className="text-sm text-gray-600" style={{ color: muted }}>{product.description}</p>
                    <button
                      onClick={() => handleQuoteClick(`product-${product.title}`)}
                      className="inline-flex items-center gap-2 text-sm font-semibold"
                      style={{ color: primary }}
                    >
                      {featuredCopy.ctaLabel || ''}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="py-16 px-4 border-t" style={{ backgroundColor: surfaceAlt, borderColor: highlight }}>
            <div className="max-w-6xl mx-auto grid lg:grid-cols-[1fr,1fr] gap-10 items-center">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-gray-500" style={{ color: muted }}>
                  {impactCopy.eyebrow || ''}
                </p>
                <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ color: text }}>
                  {impactCopy.title || ''}
                </h2>
                <p className="mb-6" style={{ color: muted }}>
                  {impactCopy.subtitle || ''}
                </p>
                <ul className="space-y-3">
                  {impactHighlights.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm" style={{ color: muted }}>
                      <span className="mt-1 w-2.5 h-2.5 rounded-full" style={{ backgroundColor: primary }} />
                      {item}
                    </li>
                  ))}
                </ul>
                {impactCopy.buttonLabel && (
                  <button
                    onClick={() => handleQuoteClick('impact')}
                    className="mt-6 px-5 py-3 rounded-full text-white font-semibold shadow"
                    style={ctaPrimaryStyle}
                  >
                    {impactCopy.buttonLabel}
                  </button>
                )}
              </div>
              <div className="border rounded-3xl shadow-lg p-6" style={{ borderColor: cardBorder, backgroundColor: cardBg }}>
                <img
                  src={
                    impactCopy.cardImage ||
                    'https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=1200&q=80'
                  }
                  alt={impactCopy.cardAlt || ''}
                  className="rounded-2xl mb-4 h-64 w-full object-cover"
                />
                <p className="text-sm text-gray-600" style={{ color: muted }}>
                  {impactCopy.cardText || ''}
                </p>
              </div>
            </div>
          </section>

          {showProfessionalSection && professionalSegments.length > 0 && (
          <section id="professionals" className="py-16 px-4 border-t" style={{ borderColor: highlight, backgroundColor: surface }}>
            <div className="max-w-6xl mx-auto">
              <div className="mb-10 text-center">
                <p className="text-xs uppercase tracking-[0.4em] text-gray-500" style={{ color: muted }}>
                  {professionalCopy.eyebrow || ''}
                </p>
                <h2 className="text-3xl font-bold text-gray-900" style={{ color: text }}>
                  {professionalCopy.title || ''}
                </h2>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {professionalSegments.map((segment) => (
                  <article
                    key={segment.title}
                    className="border rounded-3xl p-6"
                    style={{
                      borderColor: cardBorder,
                      background: `linear-gradient(180deg, ${cardBg}, ${surfaceAlt})`,
                    }}
                  >
                    <h3 className="text-xl font-semibold text-gray-900 mb-2" style={{ color: text }}>{segment.title}</h3>
                    <p className="text-sm text-gray-600 mb-4" style={{ color: muted }}>{segment.desc}</p>
                    <button
                      onClick={() => handleQuoteClick(`segment-${segment.title}`)}
                      className="text-sm font-semibold"
                      style={{ color: primary }}
                    >
                      {(professionalCopy.ctaLabelPrefix || '') + ' ' + segment.title}{' '}
                      {professionalCopy.ctaLabelSuffix || ''}
                    </button>
                  </article>
                ))}
              </div>
            </div>
          </section>
          )}

          {showTestimonialSection && (testimonialCopy.quote || testimonialCopy.author || testimonialCopy.eyebrow) && (
          <section className="py-16 px-4 border-t" style={{ borderColor: highlight, backgroundColor: surface }}>
            <div className="max-w-4xl mx-auto text-center space-y-4">
              <p className="text-xs uppercase tracking-[0.4em] text-gray-500" style={{ color: muted }}>
                {testimonialCopy.eyebrow || ''}
              </p>
              <blockquote className="text-2xl font-semibold text-gray-900" style={{ color: text }}>
                {testimonialCopy.quote || ''}
              </blockquote>
              <p className="text-sm text-gray-600" style={{ color: muted }}>
                {testimonialCopy.author || ''}
              </p>
            </div>
          </section>
          )}

          {aboutHasContent && (
          <section id="about" className="py-16 px-4 border-t" style={{ borderColor: highlight, backgroundColor: surfaceAlt }}>
            <div className="max-w-6xl mx-auto grid gap-8 lg:grid-cols-[1.1fr,1fr] items-center">
              <div className="space-y-4">
                <p className="text-xs uppercase tracking-[0.4em] text-gray-500" style={{ color: muted }}>
                  {aboutSection.eyebrow || ''}
                </p>
                <h2 className="text-3xl font-bold text-gray-900" style={{ color: text }}>
                  {aboutSection.title || ''}
                </h2>
                <p className="text-sm text-gray-600" style={{ color: muted }}>
                  {aboutSection.subtitle || ''}
                </p>
                <p className="text-sm leading-relaxed text-gray-700" style={{ color: muted }}>
                  {aboutSection.story || ''}
                </p>
              </div>
              {aboutImages.length > 0 && (
                <div className="grid grid-cols-2 gap-3">
                  {aboutImages.map((img, idx) => (
                    <div
                      key={`${img}-${idx}`}
                      className="overflow-hidden rounded-2xl border shadow-sm"
                      style={{ borderColor: cardBorder, backgroundColor: cardBg }}
                    >
                      <img src={img} alt={`about-${idx}`} className="h-40 w-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
          )}

          {showWhySection && (whyChoose.length > 0 || whyCopy.title || whyCopy.eyebrow) && (
          <section id="why" className="py-16 px-4 border-t" style={{ borderColor: highlight, backgroundColor: surface }}>
            <div className="max-w-6xl mx-auto">
              <div className="mb-10 text-center">
                <p className="text-xs uppercase tracking-[0.4em] text-gray-500" style={{ color: muted }}>
                  {whyCopy.eyebrow || ''}
                </p>
                <h2 className="text-3xl font-bold text-gray-900" style={{ color: text }}>
                  {whyCopy.title || ''}
                </h2>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {whyChoose.map((item) => (
                  <article
                    key={item.title}
                    className="border rounded-3xl p-6"
                    style={{ borderColor: cardBorder, backgroundColor: surfaceAlt }}
                  >
                    <h3 className="text-xl font-semibold text-gray-900" style={{ color: text }}>{item.title}</h3>
                    <p className="text-sm text-gray-600 mt-2" style={{ color: muted }}>{item.desc}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
          )}

          {showBlogSection && (blogPosts.length > 0 || blogCopy.title || blogCopy.eyebrow) && (
          <section id="resources" className="py-16 px-4 border-t" style={{ backgroundColor: surfaceAlt, borderColor: highlight }}>
            <div className="max-w-6xl mx-auto">
              <div className="mb-10 text-center">
                <p className="text-xs uppercase tracking-[0.4em] text-gray-500" style={{ color: muted }}>
                  {blogCopy.eyebrow || ''}
                </p>
                <h2 className="text-3xl font-bold text-gray-900" style={{ color: text }}>
                  {blogCopy.title || ''}
                </h2>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {blogPosts.map((post) => (
                  <article
                    key={post.title}
                    className="border rounded-3xl p-6 shadow-sm"
                    style={{ borderColor: cardBorder, backgroundColor: cardBg }}
                  >
                    <p className="text-xs uppercase tracking-[0.4em] text-gray-500 mb-2" style={{ color: muted }}>{post.date}</p>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" style={{ color: text }}>{post.title}</h3>
                    <button
                      onClick={() => handleQuoteClick(`blog-${post.title}`)}
                      className="text-sm font-semibold"
                      style={{ color: primary }}
                    >
                      {blogCopy.ctaLabel || ''}
                    </button>
                  </article>
                ))}
              </div>
            </div>
          </section>
          )}

          {showContactSection &&
            (contactInfo.title || contactInfo.description || contactInfo.email || contactInfo.whatsapp) && (
          <section
            id="contact"
            className="py-16 px-4 border-t"
            style={{ background: `linear-gradient(120deg, ${gradientFrom}, ${gradientTo})`, borderColor: highlight }}
          >
            <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10">
              <div>
                <p className="text-xs uppercase text-gray-500 tracking-[0.3em] mb-2" style={{ color: muted }}>
                  {contactCopy.eyebrow || ''}
                </p>
                <h3 className="text-3xl font-bold text-gray-900 mb-4" style={{ color: text }}>{contactInfo.title}</h3>
                <p className="mb-6" style={{ color: muted }}>
                  {contactInfo.description}
                </p>
                <div className="space-y-2" style={{ color: text }}>
                  <p><strong>{contactCopy.labels?.email || ''}:</strong> {contactInfo.email}</p>
                  <p><strong>{contactCopy.labels?.whatsapp || ''}:</strong> {contactInfo.whatsapp}</p>
                  {contactInfo.hours && <p><strong>{contactCopy.labels?.hours || ''}:</strong> {contactInfo.hours}</p>}
                  {contactInfo.location && <p><strong>{contactCopy.labels?.location || ''}:</strong> {contactInfo.location}</p>}
                </div>
              </div>
              <div className="border rounded-3xl shadow-lg p-6 space-y-4" style={{ borderColor: cardBorder, backgroundColor: cardBg }}>
                <p className="text-sm font-semibold text-gray-700" style={{ color: text }}>
                  {contactInfo.ctaCard?.text}
                </p>
                <button
                  onClick={() => handleQuoteClick('contact-card')}
                  className="w-full rounded-full text-white font-semibold py-3 shadow hover:shadow-lg transition"
                  style={ctaPrimaryStyle}
                >
                  {contactInfo.ctaCard?.button || ''}
                </button>
                <p className="text-xs text-gray-500" style={{ color: muted }}>
                  {contactCopy.ctaFootnote || ''}
                </p>
              </div>
            </div>
          </section>
          )}
        </main>
      </div>

      {galleryModal.open && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center px-4">
          <div className="relative rounded-3xl shadow-2xl max-w-3xl w-full overflow-hidden" style={{ backgroundColor: cardBg }}>
            <button
              className="absolute top-3 right-3 hover:opacity-80"
              style={{ color: muted }}
              onClick={closeGalleryModal}
              aria-label={modalsCopy.galleryClose || ''}
            >
              ×
            </button>
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.4em] text-gray-500" style={{ color: muted }}>
                  {modalsCopy.galleryLabel || ''}
                  </p>
                  <h3 className="text-xl font-semibold text-gray-900" style={{ color: text }}>{galleryModal.title}</h3>
                </div>
                <div className="text-sm text-gray-500" style={{ color: muted }}>
                  {galleryModal.current + 1}/{galleryModal.images.length}
                </div>
              </div>
              <div className="relative">
                {activeGalleryIsPdf ? (
                  <div
                    className="w-full h-72 rounded-2xl border flex flex-col items-center justify-center gap-3"
                    style={{ borderColor: cardBorder, backgroundColor: surfaceAlt }}
                  >
                    <span className="text-sm font-semibold" style={{ color: text }}>PDF</span>
                    <a
                      href={activeGalleryItem}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-semibold underline"
                      style={{ color: primary }}
                    >
                      Abrir documento
                    </a>
                  </div>
                ) : activeGalleryIsVideo ? (
                  <video
                    src={activeGalleryItem}
                    className="w-full h-72 object-cover rounded-2xl"
                    controls
                    playsInline
                  />
                ) : (
                  <img
                    src={activeGalleryItem}
                    alt={`${galleryModal.title}-${galleryModal.current}`}
                    className="w-full h-72 object-cover rounded-2xl"
                  />
                )}
                {galleryModal.images.length > 1 && (
                  <>
                    <button
                      className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full w-10 h-10 shadow"
                      style={{ backgroundColor: cardBg, color: text }}
                      onClick={() => handleGalleryNav('prev')}
                      aria-label={modalsCopy.galleryPrevLabel || ''}
                    >
                      ‹
                    </button>
                    <button
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full w-10 h-10 shadow"
                      style={{ backgroundColor: cardBg, color: text }}
                      onClick={() => handleGalleryNav('next')}
                      aria-label={modalsCopy.galleryNextLabel || ''}
                    >
                      ›
                    </button>
                  </>
                )}
              </div>
              <div className="mt-5 flex justify-between items-center">
                <button
                  onClick={() => handleQuoteClick(`modal-${galleryModal.title}`)}
                  className={`${ctaPrimary} px-5 py-2`}
                  style={ctaPrimaryStyle}
                >
                  {modalsCopy.galleryCta || ''}
                </button>
                <button onClick={closeGalleryModal} className="text-sm hover:opacity-80" style={{ color: muted }}>
                  {modalsCopy.galleryClose || ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {quoteModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="rounded-3xl shadow-2xl w-full max-w-2xl p-6 relative" style={{ backgroundColor: cardBg }}>
            <button
              className="absolute top-4 right-4 hover:opacity-80"
              style={{ color: muted }}
              onClick={closeQuoteModal}
              aria-label={modalsCopy.quoteModalCloseLabel || ''}
            >
              ×
            </button>
            <div className="flex flex-col md:flex-row gap-5">
              {quoteModal.design?.image && (
                <img
                  src={quoteModal.design.image}
                  alt={quoteModal.design.name}
                  className="md:w-1/3 w-full h-48 object-cover rounded-2xl"
                />
              )}
              <div className="flex-1 space-y-3">
                <p className="text-xs uppercase tracking-[0.4em] text-gray-500" style={{ color: muted }}>
                  {modalsCopy.quoteModalLabel || ''}
                </p>
                <h3 className="text-2xl font-semibold text-gray-900" style={{ color: text }}>{quoteModal.design?.name}</h3>
                <p className="text-sm text-gray-600" style={{ color: muted }}>{quoteModal.design?.description}</p>
                <div className="grid md:grid-cols-3 gap-3 pt-2">
                  <label className="text-sm font-semibold text-gray-700" style={{ color: text }}>
                    {wizardCopy.heightLabel || ''}
                    <input
                      type="number"
                      min="1"
                      value={quoteModal.height}
                      onChange={(e) => handleQuoteModalFieldChange('height', e.target.value)}
                      className="mt-1 w-full rounded-2xl border px-3 py-2"
                      style={{ borderColor: cardBorder }}
                    />
                  </label>
                  <label className="text-sm font-semibold text-gray-700" style={{ color: text }}>
                    {wizardCopy.widthLabel || ''}
                    <input
                      type="number"
                      min="1"
                      value={quoteModal.width}
                      onChange={(e) => handleQuoteModalFieldChange('width', e.target.value)}
                      className="mt-1 w-full rounded-2xl border px-3 py-2"
                      style={{ borderColor: cardBorder }}
                    />
                  </label>
                  <label className="text-sm font-semibold text-gray-700" style={{ color: text }}>
                    {wizardCopy.quantityLabel || ''}
                    <input
                      type="number"
                      min="1"
                      value={quoteModal.quantity}
                      onChange={(e) => handleQuoteModalFieldChange('quantity', e.target.value)}
                      className="mt-1 w-full rounded-2xl border px-3 py-2"
                      style={{ borderColor: cardBorder }}
                    />
                  </label>
                </div>
                {quoteModal.design?.categoryId === 'ventanas' && (
                  <label className="text-sm font-semibold text-gray-700 block" style={{ color: text }}>
                    {wizardCopy.seriesLabel || ''}
                    <select
                      value={quoteModal.series}
                      onChange={(e) => handleQuoteModalFieldChange('series', e.target.value)}
                      className="mt-1 w-full rounded-2xl border px-3 py-2"
                      style={{ borderColor: cardBorder }}
                    >
                      {wizardSeriesOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {quoteModalError && <p className="text-sm text-red-500">{quoteModalError}</p>}
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    onClick={handleModalAddModel}
                    className="flex-1 rounded-full text-white font-semibold py-2.5 shadow"
                    style={ctaPrimaryStyle}
                  >
                    {modalsCopy.quoteModalSave || ''}
                  </button>
                  <button
                    onClick={closeQuoteModal}
                    className="px-5 py-2.5 rounded-full border font-semibold"
                    style={{ borderColor: cardBorder, color: text, backgroundColor: surface }}
                  >
                    {modalsCopy.quoteModalCancel || ''}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {quoteFormOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeQuoteFormModal}
            aria-hidden="true"
          />
          <div
            className="relative w-full sm:max-w-5xl max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl shadow-xl p-6"
            style={{ backgroundColor: cardBg, borderColor: cardBorder }}
            role="dialog"
            aria-modal="true"
            aria-label="Cotización"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-gray-500" style={{ color: muted }}>
                  {measurementCopy.eyebrow || ''}
                </p>
                <h3 className="text-xl font-semibold" style={{ color: text }}>
                  {measurementCopy.title || 'Cotizar ahora'}
                </h3>
                <p className="text-sm" style={{ color: muted }}>
                  {measurementCopy.subtitle || ''}
                </p>
              </div>
              <button
                type="button"
                onClick={closeQuoteFormModal}
                className="rounded-full border px-3 py-1 text-xs font-semibold"
                style={{ borderColor: cardBorder, color: text }}
              >
                Cerrar
              </button>
            </div>
            <BusinessInfoForm
              selectedModels={selectedModels}
              onUpdateSelectedModels={setSelectedModels}
              catalog={formCatalog || undefined}
            />
          </div>
        </div>
      )}

      {selectedModels.length > 0 && (
        <button
          type="button"
          onClick={openQuoteFormModal}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 sm:left-auto sm:right-8 sm:translate-x-0 z-40 px-6 py-3 rounded-full shadow-lg border font-semibold flex items-center gap-2"
          style={{ backgroundColor: surface, borderColor: cardBorder, color: text }}
        >
          {floatingQuoteLabel} ({selectedModels.length})
          <span aria-hidden="true">→</span>
        </button>
      )}

      {wizardOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" onClick={closeWizard}>
          <div
            className="rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6"
            style={{ backgroundColor: cardBg }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.4em]" style={{ color: primary }}>
                  {wizardCopy.title || ''}
                </p>
                <h3 className="text-2xl font-semibold text-gray-900" style={{ color: text }}>
                  {wizardCopy.subtitle || ''}
                </h3>
                <p className="text-sm text-gray-500" style={{ color: muted }}>
                  {wizardCopy.description || ''}
                </p>
              </div>
              <button
                className="text-gray-400 hover:opacity-80 text-2xl"
                style={{ color: muted }}
                onClick={closeWizard}
                aria-label={wizardCopy.closeLabel || ''}
              >
                ×
              </button>
            </div>
            <div className="flex items-center gap-3 mt-6">
              {wizardSteps.map((step) => (
                <div key={step.id} className="flex-1">
                  <div
                    className={`h-2 rounded-full ${
                      wizardStep >= step.id ? 'bg-emerald-500' : 'bg-emerald-100'
                    }`}
                    style={{ backgroundColor: wizardStep >= step.id ? primary : surfaceAlt }}
                  />
                  <p
                    className={`text-xs mt-1 ${wizardStep === step.id ? 'font-semibold' : ''}`}
                    style={{ color: wizardStep === step.id ? primary : muted }}
                  >
                    {step.title}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-6">{renderWizardStep()}</div>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              {wizardStep > 1 && (
                <button
                  type="button"
                  onClick={goToPrevWizardStep}
                  className={`${ctaSecondary} w-full sm:w-auto px-5 py-2`}
                  style={ctaSecondaryStyle}
                >
                  {wizardCopy.prevLabel || ''}
                </button>
              )}
              <button
                type="button"
                onClick={goToNextWizardStep}
                disabled={!wizardCanProceed()}
                className={`${ctaPrimary} w-full sm:flex-1 px-5 py-2 disabled:opacity-60`}
                style={ctaPrimaryStyle}
              >
                {wizardStep === totalWizardSteps
                  ? wizardCopy.finishLabel || ''
                  : wizardCopy.nextLabel || ''}
              </button>
              <button
                type="button"
                onClick={closeWizard}
                className="text-sm w-full sm:w-auto"
                style={{ color: muted }}
              >
                {wizardCopy.closeLabel || ''}
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setAssistantOpen(true)}
        className="fixed right-6 bottom-6 z-40 px-4 sm:px-5 py-3 rounded-full shadow-lg text-white font-semibold hover:shadow-2xl transition"
        style={ctaPrimaryStyle}
      >
        {assistantCopy.buttonLabel || ''}
      </button>

      {assistantOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center px-4">
          <div
            className={`rounded-3xl shadow-2xl w-full ${
              assistantSize === 'lg' ? 'max-w-4xl h-[85vh]' : 'max-w-lg h-[80vh]'
            } flex flex-col transition-all`}
            style={{ backgroundColor: cardBg }}
          >
            <div className="relative overflow-hidden border-b px-6 py-4" style={{ borderColor: cardBorder }}>
              <div className="absolute inset-0 opacity-20 pointer-events-none">
                <svg width="100%" height="100%" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="assistantGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor={primary} />
                      <stop offset="100%" stopColor={primaryDark} />
                    </linearGradient>
                  </defs>
                  <circle cx="20%" cy="20%" r="180" fill="url(#assistantGradient)" />
                  <circle cx="80%" cy="10%" r="120" fill={primaryDark} opacity="0.4" />
                </svg>
              </div>
              <div className="flex items-center justify-between relative">
                <div>
                  <p className="text-xs uppercase tracking-[0.4em] text-gray-200">
                    {assistantCopy.label || ''}
                  </p>
                  <h3 className="text-lg font-semibold text-white drop-shadow">
                    {assistantCopy.title || brandName}
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setAssistantSize((prev) => (prev === 'lg' ? 'md' : 'lg'))}
                    className="text-white/80 hover:text-white text-sm border border-white/40 rounded-full px-3 py-1"
                  >
                    {assistantSize === 'lg'
                      ? assistantCopy.compactLabel || ''
                      : assistantCopy.wideLabel || ''}
                  </button>
                  <button
                    onClick={() => setAssistantOpen(false)}
                    className="text-white/90 hover:text-white text-xl leading-none"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" style={{ backgroundColor: surfaceAlt }}>
              {assistantMessages.map((msg, idx) => (
                <div
                  key={`${msg.role}-${idx}-${msg.content.slice(0, 8)}`}
                  className={`flex ${msg.role === 'assistant' ? 'justify-start' : 'justify-end'}`}
                >
                  <div
                    className="rounded-2xl px-4 py-3 text-sm shadow-sm border"
                    style={{
                      maxWidth: '80%',
                      backgroundColor: msg.role === 'assistant' ? cardBg : primary,
                      borderColor: msg.role === 'assistant' ? cardBorder : primary,
                      color: msg.role === 'assistant' ? text : '#ffffff',
                    }}
                  >
                    {msg.content}
                  </div>
                </div>
              ))}
              {assistantError && <p className="text-xs text-red-500">{assistantError}</p>}
            </div>

            <div className="border-t px-6 py-4 space-y-3 rounded-b-3xl" style={{ borderColor: cardBorder, backgroundColor: cardBg }}>
              <textarea
                value={assistantInput}
                onChange={(e) => setAssistantInput(e.target.value)}
                rows={assistantSize === 'lg' ? 3 : 2}
                className="w-full rounded-2xl border px-3 py-2 text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: cardBorder }}
                placeholder={assistantCopy.inputPlaceholder || ''}
              />
              <button
                type="button"
                onClick={sendAssistantMessage}
                disabled={assistantLoading}
                className="w-full rounded-full text-white font-semibold py-2.5 shadow hover:shadow-lg transition disabled:opacity-60"
                style={ctaPrimaryStyle}
              >
                {assistantLoading ? assistantCopy.loadingLabel || '' : assistantCopy.sendLabel || ''}
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="pt-10 pb-6" style={{ backgroundColor: primaryDark, color: heroMuted }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 mb-10">
            <div>
              <h2 className="text-2xl font-bold mb-2" style={{ color: heroText }}>{brandName}</h2>
              <p className="text-sm leading-relaxed" style={{ color: heroMuted }}>
                {config.footer?.description || ''}
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-3" style={{ color: heroText }}>
                {footerCopy.navTitle || ''}
              </h3>
              <ul className="space-y-2 text-sm">
                {navigationItems.map((item) => (
                  <li key={item.href}>
                    <a href={item.href} className="transition hover:opacity-80" style={{ color: heroMuted }}>
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-3" style={{ color: heroText }}>
                {footerCopy.contactTitle || ''}
              </h3>
              {contactInfo.email && <p className="text-sm" style={{ color: heroMuted }}>{contactInfo.email}</p>}
              {contactInfo.whatsapp && (
                <p className="text-sm" style={{ color: heroMuted }}>
                  {contactCopy.labels?.whatsapp || ''}
                  {contactCopy.labels?.whatsapp ? ': ' : ''}
                  {contactInfo.whatsapp}
                </p>
              )}
              {contactInfo.location && <p className="text-sm" style={{ color: heroMuted }}>{contactInfo.location}</p>}
            </div>
          </div>
          <div className="border-t pt-6 text-center text-sm" style={{ borderColor: withAlpha(heroMuted, '33'), color: heroMuted }}>
            © {new Date().getFullYear()} {brandName}. {footerCopy.rightsText || ''}
          </div>
        </div>
      </footer>

      <a
        href={config.whatsappFloat.link}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleWhatsAppClick}
        className="fixed bottom-6 left-6 text-white p-3 rounded-full shadow-lg z-50 hover:opacity-90"
        style={{ backgroundColor: primary }}
      >
        <FontAwesomeIcon icon={faWhatsapp} size="lg" />
      </a>
    </>
  );
};

export default LandingPage;
